import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateNote, deleteNote } from '@/api/notes';
import type { Note } from '@/utils/types';

type EditFormProps = {
  note: Note;
};

export default function EditForm({ note }: EditFormProps) {
  const navigate = useNavigate();
  const [title, setTitle] = useState(note.title);
  const [content, setContent] = useState(note.content);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const trimmedTitle = title.trim();
    const trimmedContent = content.trim();
    if (!trimmedTitle) {
      setError('Title is required');
      return;
    }
    if (!trimmedContent) {
      setError('Content is required');
      return;
    }

    setSubmitting(true);
    try {
      await updateNote(note.id, { title: trimmedTitle, content: trimmedContent });
      navigate('/backend', { state: { noteUpdated: true } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update note');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm('Are you sure you want to delete this note?');
    if (!confirmed) return;
    setError(null);
    setDeleting(true);
    try {
      await deleteNote(note.id);
      navigate('/backend', { state: { noteDeleted: true } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete note');
    } finally {
      setDeleting(false);
    }
  };

  const busy = submitting || deleting;

  return (
    <form onSubmit={handleSubmit} className="mt-6 max-w-xl space-y-4">
      {error && (
        <div
          className="rounded-md bg-red-900/30 border border-red-700 text-red-300 px-4 py-2 text-sm"
          role="alert"
        >
          {error}
        </div>
      )}
      <div>
        <label htmlFor="note-title" className="block text-sm font-medium text-gray-300 mb-1">
          Title <span className="text-red-400">*</span>
        </label>
        <input
          id="note-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          className="w-full rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-white placeholder-gray-500 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          placeholder="Note title"
          disabled={busy}
        />
      </div>
      <div>
        <label htmlFor="note-content" className="block text-sm font-medium text-gray-300 mb-1">
          Content <span className="text-red-400">*</span>
        </label>
        <textarea
          id="note-content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={4}
          className="w-full rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-white placeholder-gray-500 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-y"
          placeholder="Note content"
          disabled={busy}
        />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center justify-center rounded-md font-medium bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm px-4 py-2 transition-colors focus:outline-none disabled:opacity-50 disabled:pointer-events-none"
        >
          {submitting ? 'Updating…' : 'Update Note'}
        </button>
        <button
          type="button"
          onClick={handleDelete}
          disabled={busy}
          className="inline-flex items-center justify-center rounded-md font-medium bg-red-600 hover:bg-red-700 text-white shadow-sm px-4 py-2 transition-colors focus:outline-none disabled:opacity-50 disabled:pointer-events-none"
        >
          {deleting ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </form>
  );
}
